/**
 * Handler for BE-returned membership status 403s.
 *
 * When a firm is SUSPENDED, write requests are 403'd with code
 * `MEMBERSHIP_SUSPENDED` and a human-readable BE-provided message.
 * CANCELLED firms receive `MEMBERSHIP_CANCELLED` instead.
 *
 * This module fires a single toast with an actionable CTA so the user
 * understands why their write failed and what to do next.
 */

import { toast } from '@/lib/toast';

export const MEMBERSHIP_SUSPENDED_CODE = 'MEMBERSHIP_SUSPENDED';
export const MEMBERSHIP_CANCELLED_CODE = 'MEMBERSHIP_CANCELLED';

const REACTIVATION_CONTACT_EMAIL = 'tom@vale.so';

function buildReactivationMailto(): string {
  const subject = 'Reactivate Vale account';
  const body =
    'Hi Tom,\n\nWe would like to reactivate our Vale account.\n\nThanks,';
  return `mailto:${REACTIVATION_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Returns true if the error was a recognized membership 403 and a toast
 * was emitted. Callers can use the boolean to suppress duplicate error
 * surfacing if they wish.
 */
export function handleMembershipError(
  status: number,
  code: string | undefined,
  message: string | undefined
): boolean {
  if (status !== 403 || !code) return false;
  if (!isBrowser()) return false;

  const fallbackMessage =
    code === MEMBERSHIP_SUSPENDED_CODE
      ? 'Your account is suspended. Update your billing details to restore access.'
      : code === MEMBERSHIP_CANCELLED_CODE
        ? 'Your subscription has ended. Contact Vale to reactivate.'
        : null;

  if (code === MEMBERSHIP_SUSPENDED_CODE) {
    toast.error(message || fallbackMessage, {
      id: 'membership-suspended',
      duration: 8000,
      action: {
        label: 'Manage billing',
        onClick: () => {
          window.location.href = '/account/billing';
        }
      }
    });
    return true;
  }

  if (code === MEMBERSHIP_CANCELLED_CODE) {
    toast.error(message || fallbackMessage, {
      id: 'membership-cancelled',
      duration: 10000,
      action: {
        label: 'Contact us',
        onClick: () => {
          window.location.href = buildReactivationMailto();
        }
      }
    });
    return true;
  }

  return false;
}
